import { type GridColumn } from "@common/controls/Grid/types";
import GridView, { type GridViewProps } from "@common/controls/GridView/component";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { formatMoneyDisplay } from "@common/lib/format";
import Network from "@common/modules/network/types";
import { getUserDisplayName } from "@common/modules/user/lib";
import * as React from "react";

type CommissionVolumeDrillDownGridProps = {
  data: Network.CommissionRules.Api.CommissionVolume[];
  onRowClick: (item: Network.CommissionRules.Api.CommissionVolume) => void;
};

export const CommissionVolumeDrillDownGrid = (props: CommissionVolumeDrillDownGridProps) => {
  const volumeDrillDownGridData = React.useMemo((): GridViewProps<
    Network.CommissionRules.Api.CommissionVolume,
    Network.CommissionRules.Api.CommissionVolume
  > => {
    const i18n = useI18n();

    const items: Network.CommissionRules.Api.CommissionVolume[] = Object.values(
      props.data.reduce((acc, curr) => {
        const { userId, volume } = curr;
        if (!acc[userId]) {
          acc[userId] = { ...curr };
        } else {
          acc[userId].volume += volume;
        }
        return acc;
      }, {} as Record<string, Network.CommissionRules.Api.CommissionVolume>)
    );

    const columns: GridColumn<Network.CommissionRules.Api.CommissionVolume>[] = [
      {
        key: "userId",
        name: i18n.name,
        dataType: "string",
        data: (x) => getUserDisplayName({ userFirstName: x.userFirstName, userLastName: x.userLastName }),
        qa: "Name"
      },
      {
        key: "volume",
        name: i18n.amount,
        dataType: "string",
        data: (x) => formatMoneyDisplay(x.volume, x.currencyCode),
        qa: "Amount"
      }
    ];

    return {
      idKey: "userId",
      columns: columns,
      onRowClick: props.onRowClick,
      data: sort(items, { key: "userFirstName" }),
      isLoading: false,
      toolbar: {
        showAdd: false,
        showSearch: false
      }
    };
  }, [props.data, props.onRowClick]);

  return <GridView pageSize={10} {...volumeDrillDownGridData} idKey="userId" isLoading={false} useSearchUrl={false} />;
};
